import React from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@apollo/client';
import {
  IFormatSettingConfigQuery,
  IFormatSettingConfigQueryVariables,
  IFormatSettingEditMutation,
  IFormatSettingEditMutationVariables,
  PermissionKey,
} from '../../../../utils/apollo/graphqlSchema';
import {
  FORMAT_SETTING_EDIT,
  GET_FORMAT_SETTING_CONFIG,
} from '../../../../utils/apollo/permission';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { assertNotNil } from '../../../../utils/types';
import { formatSettingSchema, IFormatSettingState } from './types';
import {
  FormatValueDropdown,
  VerticalField,
  FieldHint,
  FieldLabel,
} from '../../../../components/Form';
import {
  dateDisplayFormatSchema,
  globalFormat,
} from '../../../../utils/formatting';
import usePermission from '../../../../utils/hooks/permissionHooks';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { useAppDispatch } from '../../../../redux/hooks';
import { setFormatSetting } from '../../../../redux/slices/bootstrap';
import toastMessage from '../../../../utils/toastMessage';
import { generateFormatsOptions } from './utils';
import InformationalTooltip from '../../../../components/InformationalTooltip';

const StyledForm = styled.form`
  margin-top: 24px;
`;
const FieldsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(120px, 240px);
  gap: 24px;
`;
const FlexFieldLabel = styled(FieldLabel)`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;
const ActionsWrapper = styled.div`
  display: flex;
  column-gap: 16px;
  margin-top: 24px;
`;

const EXAMPLE_DATE = '12/13/2024 10:43 PM';
const EXAMPLE_AMOUNT = 12345;

const FormatSettingForm = () => {
  const dispatch = useAppDispatch();
  const { checkIsPermitted } = usePermission();
  const isPermittedToEditFormat = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminSettingEdit],
  });

  const currentFormatSetting = useBootstrapSelector('formatSetting');
  assertNotNil(currentFormatSetting);

  const { data, loading } = useQuery<
    IFormatSettingConfigQuery,
    IFormatSettingConfigQueryVariables
  >(GET_FORMAT_SETTING_CONFIG);

  const [updateFormatSetting] = useMutation<
    IFormatSettingEditMutation,
    IFormatSettingEditMutationVariables
  >(FORMAT_SETTING_EDIT);

  const formatOptions = generateFormatsOptions(
    data?.formatSettingConfig,
    currentFormatSetting.locale,
  );

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isDirty },
    reset,
    watch,
  } = useForm<IFormatSettingState>({
    resolver: zodResolver(formatSettingSchema),
    defaultValues: {
      currency: currentFormatSetting.currency,
      date: currentFormatSetting.date,
      locale: currentFormatSetting.locale,
      time: currentFormatSetting.time,
    },
  });

  const localeFormValue = watch('locale');

  const onSubmitForm = handleSubmit(async (formData) => {
    const { data } = await updateFormatSetting({
      variables: {
        input: formData,
      },
    });
    if (printGraphqlErrors(data?.formatSettingEdit?.errors)) {
      return;
    }
    assertNotNil(data?.formatSettingEdit?.entity);
    const { entity } = data.formatSettingEdit;
    dispatch(setFormatSetting(entity));
    globalFormat.updateFormatConfig(entity);
    toastMessage({ message: 'Format setting has been updated.', type: 'info' });
    reset(formData);
  });

  const handleReset = () => {
    reset();
  };

  const disabled = loading || !formatOptions;

  return (
    <StyledForm onSubmit={onSubmitForm}>
      <FieldsContainer>
        <Controller
          name="date"
          control={control}
          render={({ field, fieldState }) => (
            <VerticalField noMargin={true}>
              <FieldLabel>Date</FieldLabel>
              <FormatValueDropdown
                value={field.value}
                onChange={field.onChange}
                options={formatOptions?.dateOptions ?? []}
                hasError={Boolean(fieldState.error)}
                readOnly={!isPermittedToEditFormat}
                disabled={disabled}
              />
              <FieldHint>
                Example:{' '}
                {globalFormat.formatDate(EXAMPLE_DATE, {
                  dateFormat: dateDisplayFormatSchema.parse(field.value),
                })}
              </FieldHint>
            </VerticalField>
          )}
        />
        <Controller
          name="time"
          control={control}
          render={({ field, fieldState }) => (
            <VerticalField noMargin={true}>
              <FieldLabel>Time</FieldLabel>
              <FormatValueDropdown
                value={field.value}
                onChange={field.onChange}
                options={formatOptions?.timeOptions ?? []}
                hasError={Boolean(fieldState.error)}
                readOnly={!isPermittedToEditFormat}
                disabled={disabled}
              />
              <FieldHint>
                Example: {globalFormat.formatTime(EXAMPLE_DATE, field.value)}
              </FieldHint>
            </VerticalField>
          )}
        />
        <Controller
          name="locale"
          control={control}
          render={({ field, fieldState }) => (
            <VerticalField noMargin={true}>
              <FieldLabel>Number</FieldLabel>
              <FormatValueDropdown
                value={field.value}
                onChange={field.onChange}
                options={formatOptions?.localeOptions ?? []}
                hasError={Boolean(fieldState.error)}
                readOnly={!isPermittedToEditFormat}
                disabled={disabled}
              />
              <FieldHint>
                Example:{' '}
                {globalFormat.formatNumber(EXAMPLE_AMOUNT, {
                  style: 'decimal',
                  locale: field.value,
                  decimalPlace: 2,
                })}
              </FieldHint>
            </VerticalField>
          )}
        />
        <Controller
          name="currency"
          control={control}
          render={({ field, fieldState }) => (
            <VerticalField noMargin={true}>
              <FlexFieldLabel>
                Currency
                <InformationalTooltip
                  tooltipOption={{
                    content:
                      'Selecting a currency from the dropdown will display its symbol only.' +
                      ' Kubit does not perform any currency conversion.',
                  }}
                  size={14}
                />
              </FlexFieldLabel>
              <FormatValueDropdown
                value={field.value}
                onChange={field.onChange}
                options={formatOptions?.currencyOptions ?? []}
                hasError={Boolean(fieldState.error)}
                readOnly={!isPermittedToEditFormat}
                disabled={disabled}
              />
              <FieldHint>
                Example:{' '}
                {globalFormat.formatCurrency({
                  value: EXAMPLE_AMOUNT,
                  locale: localeFormValue,
                  currency: field.value,
                })}
              </FieldHint>
            </VerticalField>
          )}
        />
      </FieldsContainer>
      {isPermittedToEditFormat && (
        <ActionsWrapper>
          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !isDirty}
            trackContext="format settings"
            trackAction="save"
          >
            Save
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={handleReset}
            disabled={isSubmitting || !isDirty}
            isOutlined={true}
            trackContext="format settings"
            trackAction="reset"
          >
            Reset
          </SecondaryButton>
        </ActionsWrapper>
      )}
    </StyledForm>
  );
};

export default FormatSettingForm;
